/**
 * Configuration for Cache Admin Page
 * Only users listed here can access the Cache Admin page
 */

// Authorized users who can access the Cache Admin page
// Add or remove email addresses as needed
export const CACHE_ADMIN_AUTHORIZED_USERS: string[] = [
  "spathipati@microsoft.com",
  "amkha@microsoft.com",
  "surchaurasia@microsoft.com",
  "kanishkmalik@microsoft.com"
];

// Cache service definitions
export interface CacheService {
  id: string;
  name: string;
  description: string;
  type: "redis" | "memory" | "both";
  apiEndpoint: string;
  icon: string;
  category: "page-cache" | "business-logic" | "all";
  group: "product" | "health" | "customer" | "other" | "danger";
  affectedPages?: string[];
}

// Individual cache services that can be cleared
export const CACHE_SERVICES: CacheService[] = [
  // Danger Zone - Full cache clear
  {
    id: "all-redis",
    name: "All Redis Cache",
    description: "Clears the entire Redis database. Use with caution - this will affect all users and all pages.",
    type: "redis",
    apiEndpoint: "common/removeAllRedisCacheAtOnce",
    icon: "Database",
    category: "all",
    group: "danger",
    affectedPages: ["All Pages"]
  },

  // ==================== EXPLORE PRODUCTS PAGE ====================
  {
    id: "product-all",
    name: "All Product Cache",
    description: "Clears all product caches at once.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=all",
    icon: "ProductVariant",
    category: "page-cache",
    group: "product",
    affectedPages: ["Explore Products", "Product View"]
  },
  {
    id: "product-metrics",
    name: "Product Metrics",
    description: "Overview grid data.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=metrics",
    icon: "BarChart",
    category: "page-cache",
    group: "product",
    affectedPages: ["Overview Tab"]
  },
  {
    id: "product-cards",
    name: "Cards & Graphs",
    description: "Charts and visualizations.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=cards",
    icon: "Chart",
    category: "page-cache",
    group: "product",
    affectedPages: ["Overview Tab"]
  },
  {
    id: "product-support",
    name: "Support Data",
    description: "Tickets and cases.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=support",
    icon: "Support",
    category: "page-cache",
    group: "product",
    affectedPages: ["Support Tab"]
  },
  {
    id: "product-features",
    name: "Features",
    description: "Feature adoption data.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=features",
    icon: "Lightbulb",
    category: "page-cache",
    group: "product",
    affectedPages: ["Features Tab"]
  },
  {
    id: "product-partners",
    name: "Partners",
    description: "Partner influence data.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=partners",
    icon: "Partnership",
    category: "page-cache",
    group: "product",
    affectedPages: ["Partners Tab"]
  },
  {
    id: "product-toptenants",
    name: "Top Tenants",
    description: "Top 10 tenants data.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=toptenants",
    icon: "Building",
    category: "page-cache",
    group: "product",
    affectedPages: ["Customers Tab"]
  },
  {
    id: "product-topcustomers",
    name: "Top Customers",
    description: "Top 200 customers.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=topcustomers",
    icon: "PeopleGroup",
    category: "page-cache",
    group: "product",
    affectedPages: ["Customers Tab"]
  },
  {
    id: "product-crm",
    name: "CRM Info",
    description: "CRM product info.",
    type: "redis",
    apiEndpoint: "product/clearCache?cacheType=crm",
    icon: "CRM",
    category: "page-cache",
    group: "product",
    affectedPages: ["CRM Tab"]
  },

  // ==================== OTHER ====================
  {
    id: "close-the-loop",
    name: "Close The Loop",
    description: "Feature requests and evidence.",
    type: "memory",
    apiEndpoint: "closetheloop/clear-cache",
    icon: "Sync",
    category: "page-cache",
    group: "other",
    affectedPages: ["Close The Loop"]
  }
];

// Group services by category for display
export const getCacheServicesByCategory = () => {
  const pageCache = CACHE_SERVICES.filter(s => s.category === "page-cache");
  const allCache = CACHE_SERVICES.filter(s => s.category === "all");
  return { pageCache, allCache };
};

// Helper function to check if a user is authorized
export const isUserAuthorizedForCacheAdmin = (userEmail: string | null | undefined): boolean => {
  if (!userEmail) return false;
  const normalizedEmail = userEmail.toLowerCase().trim();
  return CACHE_ADMIN_AUTHORIZED_USERS.some(
    authorizedEmail => authorizedEmail.toLowerCase().trim() === normalizedEmail
  );
};
